import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, X, ArrowRight, Zap, ClipboardList } from "lucide-react";

const SLIDE_LEFT = { initial: { opacity: 0, x: -20 }, whileInView: { opacity: 1, x: 0 }, viewport: { once: true } };
const SLIDE_RIGHT = { initial: { opacity: 0, x: 20 }, whileInView: { opacity: 1, x: 0 }, viewport: { once: true } };

export const ComparisonSection = () => (
  <section className="section-padding bg-[#F8F9FA]" data-testid="comparison-section">
    <div className="container-max">
      <div className="text-center mb-14">
        <span className="text-xs tracking-[0.2em] uppercase font-bold text-[#FF5A1F] mb-2 block">Two Ways to Get Your Price</span>
        <h2 className="text-3xl md:text-4xl font-['Outfit'] font-bold tracking-tight text-[#0F172A] mt-2">
          Instant Estimate vs Free Survey
        </h2>
        <p className="text-slate-600 mt-4 max-w-2xl mx-auto">
          Small move? Get a price right now. Bigger job? We'll come out and give you an exact quote — completely free.
        </p>
      </div>

      <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
        {/* Instant Estimate */}
        <motion.div
          className="bg-white border-2 border-[#FF5A1F] p-8 relative"
          {...SLIDE_LEFT}
        >
          <div className="absolute -top-3 left-6 bg-[#FF5A1F] text-white text-xs font-bold px-3 py-1 uppercase tracking-wider">
            Most Popular
          </div>
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 bg-[#FF5A1F]/10 flex items-center justify-center">
              <Zap className="w-6 h-6 text-[#FF5A1F]" />
            </div>
            <div>
              <h3 className="text-xl font-['Outfit'] font-bold text-[#0F172A]">Instant Estimate</h3>
              <p className="text-sm text-slate-500">Results in under 60 seconds</p>
            </div>
          </div>

          <ul className="space-y-3 mb-8">
            {[
              "Studio, 1 or 2 bedroom moves",
              "Price shown immediately",
              "No appointment needed",
              "Book and pay deposit online",
              "Perfect for straightforward moves",
            ].map((item) => (
              <li key={item} className="flex items-start gap-3 text-sm">
                <Check className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                <span className="text-slate-700">{item}</span>
              </li>
            ))}
            {[
              "Not suitable for 3+ bedrooms",
              "Specialist items need survey",
            ].map((item) => (
              <li key={item} className="flex items-start gap-3 text-sm">
                <X className="w-4 h-4 text-slate-300 mt-0.5 flex-shrink-0" />
                <span className="text-slate-400">{item}</span>
              </li>
            ))}
          </ul>

          <Link
            to="/instant-quote"
            className="btn-primary w-full inline-flex items-center justify-center gap-2"
            data-testid="comparison-instant-btn"
          >
            Get My Price Now
            <ArrowRight className="w-4 h-4" />
          </Link>
        </motion.div>

        {/* Full Survey */}
        <motion.div
          className="bg-white border border-slate-200 p-8"
          {...SLIDE_RIGHT}
        >
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 bg-[#0F172A]/10 flex items-center justify-center">
              <ClipboardList className="w-6 h-6 text-[#0F172A]" />
            </div>
            <div>
              <h3 className="text-xl font-['Outfit'] font-bold text-[#0F172A]">Free Home Survey</h3>
              <p className="text-sm text-slate-500">Exact quote, no obligation</p>
            </div>
          </div>

          <ul className="space-y-3 mb-8">
            {[
              "Any size property — no limits",
              "Exact price, not an estimate",
              "Surveyor visits your home",
              "Includes specialist item assessment",
              "Office and commercial moves",
              "Storage and complex logistics",
              "Completely free, no obligation",
            ].map((item) => (
              <li key={item} className="flex items-start gap-3 text-sm">
                <Check className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                <span className="text-slate-700">{item}</span>
              </li>
            ))}
          </ul>

          <Link
            to="/book-survey"
            className="btn-outline w-full inline-flex items-center justify-center gap-2"
            data-testid="comparison-survey-btn"
          >
            Book Free Survey
            <ArrowRight className="w-4 h-4" />
          </Link>
        </motion.div>
      </div>
    </div>
  </section>
);
